import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { Loader2, Plus, Trash2, ShieldAlert, X } from "lucide-react";
import { toast } from "react-hot-toast";

import useSuperAdmin from "../Services/SuperAdminServices/useSuperAdmin";
import useClients from "../Services/ClientServices/useClient";
import ClientSearchSelect from "../../Components/common/ClientSearchSelect";
/** ---- API PATHS (easy to rewire later) ---- */
const API = {
  getAllModule: "/Entitlement/v1.0/getAllModule",
  getAllService: "/Entitlement/v1.0/getAllService",
  insertModule: "/Entitlement/v1.0/insertModule",
  deleteModule: "/Entitlement/v1.0/deleteModule",
  insertService: "/Entitlement/v1.0/insertService",
  deleteService: "/Entitlement/v1.0/deleteService",
  createClientEntitlement: "/Entitlement/v1.0/createClientEntitlement",
};

/** ---- Small UI helpers ---- */
const Badge = ({ tone = "gray", children }) => {
  const cls =
    tone === "green"
      ? "bg-emerald-100 text-emerald-700"
      : tone === "red"
        ? "bg-red-100 text-red-700"
        : "bg-gray-100 text-gray-700";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-[2px] text-[10px] font-semibold ${cls}`}>
      {children}
    </span>
  );
};

const ModalShell = ({ open, title, onClose, children, tone = "default" }) => {
  if (!open) return null;
  const headerTone =
    tone === "danger"
      ? "border-red-200 bg-red-50"
      : "border-gray-100 bg-gray-50";

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-3">
      <div className="w-full max-w-lg overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
        <div className={`flex items-center justify-between border-b px-4 py-3 ${headerTone}`}>
          <div className="flex items-center gap-2">
            {tone === "danger" ? (
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white">
                <ShieldAlert className="h-4 w-4" />
              </span>
            ) : null}
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 py-3">{children}</div>
      </div>
    </div>
  );
};

/** ---- Destructive multi-step confirmation (GitHub-ish) ---- */
const DestructiveConfirm = ({
  open,
  itemLabel, // "Module" / "Service"
  nameToType,
  impactText,
  onCancel,
  onConfirm, // async
  loading,
}) => {
  const [step, setStep] = useState(1);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (open) {
      setStep(1);
      setTyped("");
    }
  }, [open]);

  const typedOk = typed.trim() === (nameToType || "").trim();

  return (
    <ModalShell
      open={open}
      title={`Delete ${itemLabel}`}
      onClose={onCancel}
      tone="danger"
    >
      {step === 1 && (
        <div className="space-y-3">
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800">
            <div className="font-semibold">This action is destructive.</div>
            <div className="mt-1 text-[11px] text-red-700">
              {impactText}
            </div>
          </div>

          <div className="text-[11px] text-gray-600">
            You are attempting to delete:{" "}
            <span className="font-semibold text-gray-900">{nameToType}</span>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setStep(2)}
              className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
            >
              I understand, continue
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-800">
            Type <span className="font-bold">{nameToType}</span> to enable deletion.
          </div>

          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={`Type "${nameToType}" exactly`}
            className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-xs text-gray-900 placeholder:text-gray-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
          />

          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>

            <button
              type="button"
              disabled={!typedOk}
              onClick={() => setStep(3)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold ${typedOk
                ? "bg-red-600 text-white hover:bg-red-700"
                : "cursor-not-allowed bg-gray-200 text-gray-500"
                }`}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-800">
            Final confirmation: this cannot be undone.
          </div>

          <div className="text-[11px] text-gray-600">
            Confirm delete <span className="font-semibold text-gray-900">{nameToType}</span>.
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>

            <button
              type="button"
              disabled={loading}
              onClick={onConfirm}
              className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold ${loading
                ? "cursor-not-allowed bg-red-300 text-white"
                : "bg-red-600 text-white hover:bg-red-700"
                }`}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Deleting…
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" /> Delete
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
};

const AdminEntitlements = () => {
  const { fetchAll } = useClients();

  const { api, AllClients } = useContext(GlobalContext);

  // ---- Load Clients (for entitlement mapping) ----
  useEffect(() => {
    const ensureClients = async () => {
      if (!AllClients || AllClients.length === 0) {
        try {
          await fetchAll();
        } catch (e) {
          // toast may be handled in context
        }
      }
    };
    ensureClients();
  }, [AllClients, fetchAll]);

  // ---- Local sub-tabs inside Entitlements ----
  const SUBTABS = [
    { key: "modules", label: "Modules" },
    { key: "services", label: "Services" },
    { key: "client_entitlements", label: "Client Entitlements" },
  ];
  const [subTab, setSubTab] = useState("modules");

  // ---- Modules ----
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);

  // ---- Services ----
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  const fetchModules = useCallback(async () => {
    setModulesLoading(true);
    try {
      const { data } = await api.post(API.getAllModule, { moduleId: "" });
      if (data?.responseCode === "00") {
        setModules(Array.isArray(data.response) ? data.response : []);
      } else {
        toast.error(data?.responseMessage || "Failed to fetch modules.");
        setModules([]);
      }
    } catch (e) {
      toast.error("Failed to fetch modules.");
      setModules([]);
    } finally {
      setModulesLoading(false);
    }
  }, [api]);

  const fetchServices = useCallback(async () => {
    setServicesLoading(true);
    try {
      const { data } = await api.post(API.getAllService, { serviceId: "" });
      if (data?.responseCode === "00") {
        setServices(Array.isArray(data.response) ? data.response : []);
      } else {
        toast.error(data?.responseMessage || "Failed to fetch services.");
        setServices([]);
      }
    } catch (e) {
      toast.error("Failed to fetch services.");
      setServices([]);
    } finally {
      setServicesLoading(false);
    }
  }, [api]);

  useEffect(() => {
    // fetch both once; keep it flexible
    fetchModules();
    fetchServices();
  }, [fetchModules, fetchServices]);

  // ---- Create Module Modal ----
  const [createModuleOpen, setCreateModuleOpen] = useState(false);
  const [createModuleConfirmOpen, setCreateModuleConfirmOpen] = useState(false);
  const [createModuleLoading, setCreateModuleLoading] = useState(false);
  const [moduleForm, setModuleForm] = useState({
    module_name: "",
    module_desc: "",
    module_price: "",
    module_duration: "Monthly",
    currency: "INR",
    module_url: "",
    icon: "",
    is_active: "1",
  });

  const moduleFormValid = useMemo(() => {
    return (
      moduleForm.module_name.trim().length > 0 &&
      moduleForm.module_desc.trim().length > 0 &&
      String(moduleForm.module_price).trim().length > 0 &&
      moduleForm.module_duration.trim().length > 0 &&
      moduleForm.currency.trim().length > 0
    );
  }, [moduleForm]);

  const submitCreateModule = async () => {
    if (!moduleFormValid) return;
    setCreateModuleLoading(true);
    try {
      const payload = { ...moduleForm };
      const { data } = await api.post(API.insertModule, payload);
      if (data?.responseCode === "00") {
        toast.success(data?.responseMessage || "Module created.");
        setCreateModuleConfirmOpen(false);
        setCreateModuleOpen(false);
        setModuleForm({
          module_name: "",
          module_desc: "",
          module_price: "",
          module_duration: "Monthly",
          currency: "INR",
          module_url: "",
          icon: "",
          is_active: "1",
        });
        await fetchModules();
      } else {
        toast.error(data?.responseMessage || "Failed to create module.");
      }
    } catch (e) {
      toast.error("Failed to create module.");
    } finally {
      setCreateModuleLoading(false);
    }
  };

  // ---- Delete Module (3-step confirm) ----
  const [deleteModuleOpen, setDeleteModuleOpen] = useState(false);
  const [deleteModuleTarget, setDeleteModuleTarget] = useState(null);
  const [deleteModuleLoading, setDeleteModuleLoading] = useState(false);

  const doDeleteModule = async () => {
    if (!deleteModuleTarget?.moduleId) return;
    setDeleteModuleLoading(true);
    try {
      const { data } = await api.post(API.deleteModule, {
        moduleId: String(deleteModuleTarget.moduleId),
      });
      if (data?.responseCode === "00") {
        toast.success(data?.responseMessage || "Module deleted.");
        setDeleteModuleOpen(false);
        setDeleteModuleTarget(null);
        await fetchModules();
      } else {
        toast.error(data?.responseMessage || "Failed to delete module.");
      }
    } catch (e) {
      toast.error("Failed to delete module.");
    } finally {
      setDeleteModuleLoading(false);
    }
  };

  // ---- Create Service Modal ----
  const [createServiceOpen, setCreateServiceOpen] = useState(false);
  const [createServiceConfirmOpen, setCreateServiceConfirmOpen] = useState(false);
  const [createServiceLoading, setCreateServiceLoading] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    service_code: "",
    service_name: "",
    service_desc: "",
    service_url: "",
    icon: "",
    is_active: "1",
    currency: "INR",
  });

  const serviceFormValid = useMemo(() => {
    return (
      serviceForm.service_code.trim().length > 0 &&
      serviceForm.service_name.trim().length > 0 &&
      serviceForm.service_desc.trim().length > 0
    );
  }, [serviceForm]);

  const submitCreateService = async () => {
    if (!serviceFormValid) return;
    setCreateServiceLoading(true);
    try {
      const payload = { ...serviceForm };
      const { data } = await api.post(API.insertService, payload);
      if (data?.responseCode === "00") {
        toast.success(data?.responseMessage || "Service created.");
        setCreateServiceConfirmOpen(false);
        setCreateServiceOpen(false);
        setServiceForm({
          service_code: "",
          service_name: "",
          service_desc: "",
          service_url: "",
          icon: "",
          is_active: "1",
          currency: "INR",
        });
        await fetchServices();
      } else {
        toast.error(data?.responseMessage || "Failed to create service.");
      }
    } catch (e) {
      toast.error("Failed to create service.");
    } finally {
      setCreateServiceLoading(false);
    }
  };

  // ---- Delete Service (3-step confirm) ----
  const [deleteServiceOpen, setDeleteServiceOpen] = useState(false);
  const [deleteServiceTarget, setDeleteServiceTarget] = useState(null);
  const [deleteServiceLoading, setDeleteServiceLoading] = useState(false);

  const doDeleteService = async () => {
    if (!deleteServiceTarget?.serviceId) return;
    setDeleteServiceLoading(true);
    try {
      const { data } = await api.post(API.deleteService, {
        serviceId: String(deleteServiceTarget.serviceId),
      });
      if (data?.responseCode === "00") {
        toast.success(data?.responseMessage || "Service deleted.");
        setDeleteServiceOpen(false);
        setDeleteServiceTarget(null);
        await fetchServices();
      } else {
        toast.error(data?.responseMessage || "Failed to delete service.");
      }
    } catch (e) {
      toast.error("Failed to delete service.");
    } finally {
      setDeleteServiceLoading(false);
    }
  };

  // ---- Create Client Entitlement ----
  const [entForm, setEntForm] = useState({
    userId: "",
    moduleId: "",
    serviceId: "",
  });
  const [entConfirmOpen, setEntConfirmOpen] = useState(false);
  const [entLoading, setEntLoading] = useState(false);

  const entValid = useMemo(() => {
    return (
      entForm.userId.trim().length > 0 &&
      entForm.moduleId.trim().length > 0 &&
      entForm.serviceId.trim().length > 0
    );
  }, [entForm]);

  const selectedClient = useMemo(
    () => (AllClients || []).find((c) => String(c.us_code) === String(entForm.userId)),
    [AllClients, entForm.userId]
  );
  const selectedModule = useMemo(
    () => modules.find((m) => String(m.moduleId) === String(entForm.moduleId)),
    [modules, entForm.moduleId]
  );
  const selectedService = useMemo(
    () => services.find((s) => String(s.serviceId) === String(entForm.serviceId)),
    [services, entForm.serviceId]
  );

  const submitEntitlement = async () => {
    if (!entValid) return;
    setEntLoading(true);
    try {
      const payload = {
        userId: String(entForm.userId),
        moduleId: String(entForm.moduleId),
        serviceId: String(entForm.serviceId),
      };

      const { data } = await api.post(API.createClientEntitlement, payload);
      if (data?.responseCode === "00") {
        toast.success(data?.responseMessage || "Entitlement created.");
        setEntConfirmOpen(false);
        setEntForm({ userId: "", moduleId: "", serviceId: "" });
      } else {
        toast.error(data?.responseMessage || "Failed to create entitlement.");
      }
    } catch (e) {
      toast.error("Failed to create entitlement.");
    } finally {
      setEntLoading(false);
    }
  };

  const statusBadge = (isActive) => {
    const active = String(isActive) === "1" || String(isActive).toUpperCase() === "Y";
    return active ? <Badge tone="green">Active</Badge> : <Badge tone="red">Inactive</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Sub tabs */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3">
          {SUBTABS.map((t) => {
            const active = subTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setSubTab(t.key)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${active
                  ? "bg-gray-900 text-white"
                  : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
              >
                {t.label}
              </button>
            );
          })}

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={fetchModules}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              {modulesLoading ? (
                <span className="inline-flex items-center gap-1">
                  <Loader2 className="h-4 w-4 animate-spin" /> Modules…
                </span>
              ) : (
                "Refresh Modules"
              )}
            </button>
            <button
              type="button"
              onClick={fetchServices}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              {servicesLoading ? (
                <span className="inline-flex items-center gap-1">
                  <Loader2 className="h-4 w-4 animate-spin" /> Services…
                </span>
              ) : (
                "Refresh Services"
              )}
            </button>
          </div>
        </div>

        {/* Modules */}
        {subTab === "modules" && (
          <div className="p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-gray-900">Modules</div>
                <div className="mt-1 text-[11px] text-gray-500">
                  Manage application modules. Deletion requires multi-step confirmation.
                </div>
              </div>

              <button
                type="button"
                onClick={() => setCreateModuleOpen(true)}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
              >
                <Plus className="h-4 w-4" />
                Create Module
              </button>
            </div>

            <div className="mt-3 overflow-auto rounded-lg border border-gray-200">
              <div className="min-w-[980px]">
                <div className="grid grid-cols-12 gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 text-[11px] font-semibold text-gray-600">
                  <div className="col-span-2">Module Name</div>
                  <div className="col-span-4">Description</div>
                  <div className="col-span-1">Price</div>
                  <div className="col-span-1">Duration</div>
                  <div className="col-span-1">Currency</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>

                {modulesLoading ? (
                  <div className="flex items-center justify-center py-10 text-xs text-gray-500">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading modules…
                  </div>
                ) : modules.length === 0 ? (
                  <div className="px-4 py-8 text-center text-xs text-gray-500">
                    No modules found.
                  </div>
                ) : (
                  modules.map((m) => (
                    <div
                      key={m.moduleId}
                      className="grid grid-cols-12 gap-2 border-b border-gray-50 px-3 py-2 text-[11px] text-gray-700 hover:bg-gray-50"
                    >
                      <div className="col-span-2 font-semibold text-gray-900">
                        {m.module_name || "-"}
                      </div>
                      <div className="col-span-4 text-gray-600 line-clamp-2">
                        {m.module_desc || "-"}
                      </div>
                      <div className="col-span-1">{m.module_price || "-"}</div>
                      <div className="col-span-1">{m.module_duration || "-"}</div>
                      <div className="col-span-1">{m.currency || "-"}</div>
                      <div className="col-span-1">{statusBadge(m.is_active)}</div>
                      <div className="col-span-2 flex justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteModuleTarget(m);
                            setDeleteModuleOpen(true);
                          }}
                          className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-100"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Services */}
        {subTab === "services" && (
          <div className="p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-gray-900">Services</div>
                <div className="mt-1 text-[11px] text-gray-500">
                  Manage service permissions/abilities. Deletion uses strict confirmations.
                </div>
              </div>

              <button
                type="button"
                onClick={() => setCreateServiceOpen(true)}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
              >
                <Plus className="h-4 w-4" />
                Create Service
              </button>
            </div>

            <div className="mt-3 overflow-auto rounded-lg border border-gray-200">
              <div className="min-w-[980px]">
                <div className="grid grid-cols-12 gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 text-[11px] font-semibold text-gray-600">
                  <div className="col-span-2">Service Code</div>
                  <div className="col-span-2">Name</div>
                  <div className="col-span-4">Description</div>
                  <div className="col-span-2">URL</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-1 text-right">Actions</div>
                </div>

                {servicesLoading ? (
                  <div className="flex items-center justify-center py-10 text-xs text-gray-500">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading services…
                  </div>
                ) : services.length === 0 ? (
                  <div className="px-4 py-8 text-center text-xs text-gray-500">
                    No services found.
                  </div>
                ) : (
                  services.map((s) => (
                    <div
                      key={s.serviceId}
                      className="grid grid-cols-12 gap-2 border-b border-gray-50 px-3 py-2 text-[11px] text-gray-700 hover:bg-gray-50"
                    >
                      <div className="col-span-2 font-semibold text-gray-900">
                        {s.service_code || "-"}
                      </div>
                      <div className="col-span-2">{s.service_name || "-"}</div>
                      <div className="col-span-4 text-gray-600 line-clamp-2">
                        {s.service_desc || "-"}
                      </div>
                      <div className="col-span-2 text-gray-600">
                        {s.service_url || "-"}
                      </div>
                      <div className="col-span-1">{statusBadge(s.is_active)}</div>
                      <div className="col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteServiceTarget(s);
                            setDeleteServiceOpen(true);
                          }}
                          className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-100"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Client Entitlements */}
        {subTab === "client_entitlements" && (
          <div className="p-4">
            <div>
              <div className="text-sm font-semibold text-gray-900">Client Entitlements</div>
              <div className="mt-1 text-[11px] text-gray-500">
                Assign a Module + Service to a Client with explicit confirmation.
              </div>
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="text-[11px] font-semibold text-gray-700">Client</div>
                <ClientSearchSelect
                  clients={AllClients || []}
                  value={entForm.userId}
                  className="w-full"
                  placeholder="Select client…"
                  onChange={(client) =>
                    setEntForm((p) => ({
                      ...p,
                      userId: client?.us_code || "",
                    }))
                  }
                />
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="text-[11px] font-semibold text-gray-700">Module</div>
                <select
                  value={entForm.moduleId}
                  onChange={(e) => setEntForm((p) => ({ ...p, moduleId: e.target.value }))}
                  className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="">Select module…</option>
                  {modules.map((m) => (
                    <option key={m.moduleId} value={m.moduleId}>
                      {m.module_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="text-[11px] font-semibold text-gray-700">Service</div>
                <select
                  value={entForm.serviceId}
                  onChange={(e) => setEntForm((p) => ({ ...p, serviceId: e.target.value }))}
                  className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="">Select service…</option>
                  {services.map((s) => (
                    <option key={s.serviceId} value={s.serviceId}>
                      {s.service_code} — {s.service_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-end">
              <button
                type="button"
                disabled={!entValid || entLoading}
                onClick={() => setEntConfirmOpen(true)}
                className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold ${!entValid || entLoading
                  ? "cursor-not-allowed bg-gray-200 text-gray-500"
                  : "bg-emerald-600 text-white hover:bg-emerald-700"
                  }`}
              >
                {entLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Assigning…
                  </>
                ) : (
                  "Create Client Entitlement"
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ---------------- CREATE MODULE MODAL ---------------- */}
      <ModalShell
        open={createModuleOpen}
        title="Create Module"
        onClose={() => setCreateModuleOpen(false)}
      >
        <div className="grid gap-3 md:grid-cols-2">
          <div className="md:col-span-1">
            <div className="text-[11px] font-semibold text-gray-700">Module Name *</div>
            <input
              value={moduleForm.module_name}
              onChange={(e) => setModuleForm((p) => ({ ...p, module_name: e.target.value }))}
              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div className="md:col-span-1">
            <div className="text-[11px] font-semibold text-gray-700">Price *</div>
            <input
              value={moduleForm.module_price}
              onChange={(e) => setModuleForm((p) => ({ ...p, module_price: e.target.value }))}
              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div className="md:col-span-2">
            <div className="text-[11px] font-semibold text-gray-700">Description *</div>
            <textarea
              value={moduleForm.module_desc}
              onChange={(e) => setModuleForm((p) => ({ ...p, module_desc: e.target.value }))}
              rows={3}
              className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div>
            <div className="text-[11px] font-semibold text-gray-700">Duration *</div>
            <select
              value={moduleForm.module_duration}
              onChange={(e) => setModuleForm((p) => ({ ...p, module_duration: e.target.value }))}
              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              <option>Monthly</option>
              <option>Quarterly</option>
              <option>Yearly</option>
            </select>
          </div>

          <div>
            <div className="text-[11px] font-semibold text-gray-700">Currency *</div>
            <input
              value={moduleForm.currency}
              onChange={(e) => setModuleForm((p) => ({ ...p, currency: e.target.value }))}
              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div className="md:col-span-1">
            <div className="text-[11px] font-semibold text-gray-700">Module URL</div>
            <input
              value={moduleForm.module_url}
              onChange={(e) => setModuleForm((p) => ({ ...p, module_url: e.target.value }))}
              placeholder="/Dashboard"
              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div className="md:col-span-1">
            <div className="text-[11px] font-semibold text-gray-700">Icon</div>
            <input
              value={moduleForm.icon}
              onChange={(e) => setModuleForm((p) => ({ ...p, icon: e.target.value }))}
              placeholder="home.svg"
              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div className="md:col-span-2 flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setCreateModuleOpen(false)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!moduleFormValid}
              onClick={() => setCreateModuleConfirmOpen(true)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold ${moduleFormValid
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "cursor-not-allowed bg-gray-200 text-gray-500"
                }`}
            >
              Continue
            </button>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        open={createModuleConfirmOpen}
        title="Confirm Module Creation"
        onClose={() => setCreateModuleConfirmOpen(false)}
      >
        <div className="space-y-3 text-[12px]">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="font-semibold text-gray-900">{moduleForm.module_name}</div>
            <div className="mt-1 text-[11px] text-gray-600 line-clamp-3">{moduleForm.module_desc}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge>{moduleForm.currency} {moduleForm.module_price}</Badge>
              <Badge>{moduleForm.module_duration}</Badge>
              <Badge tone={String(moduleForm.is_active) === "1" ? "green" : "red"}>
                {String(moduleForm.is_active) === "1" ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setCreateModuleConfirmOpen(false)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              disabled={createModuleLoading}
              onClick={submitCreateModule}
              className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold ${createModuleLoading
                ? "cursor-not-allowed bg-emerald-300 text-white"
                : "bg-emerald-600 text-white hover:bg-emerald-700"
                }`}
            >
              {createModuleLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Creating…
                </>
              ) : (
                "Confirm & Create"
              )}
            </button>
          </div>
        </div>
      </ModalShell>

      {/* ---------------- CREATE SERVICE MODAL ---------------- */}
      <ModalShell
        open={createServiceOpen}
        title="Create Service"
        onClose={() => setCreateServiceOpen(false)}
      >
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <div className="text-[11px] font-semibold text-gray-700">Service Code *</div>
            <input
              value={serviceForm.service_code}
              onChange={(e) => setServiceForm((p) => ({ ...p, service_code: e.target.value }))}
              placeholder="A001"
              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div>
            <div className="text-[11px] font-semibold text-gray-700">Service Name *</div>
            <input
              value={serviceForm.service_name}
              onChange={(e) => setServiceForm((p) => ({ ...p, service_name: e.target.value }))}
              placeholder="Campaign"
              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div className="md:col-span-2">
            <div className="text-[11px] font-semibold text-gray-700">Description *</div>
            <textarea
              value={serviceForm.service_desc}
              onChange={(e) => setServiceForm((p) => ({ ...p, service_desc: e.target.value }))}
              rows={3}
              className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div>
            <div className="text-[11px] font-semibold text-gray-700">URL</div>
            <input
              value={serviceForm.service_url}
              onChange={(e) => setServiceForm((p) => ({ ...p, service_url: e.target.value }))}
              placeholder="/WhatsApp/Manage"
              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div>
            <div className="text-[11px] font-semibold text-gray-700">Currency</div>
            <input
              value={serviceForm.currency}
              onChange={(e) => setServiceForm((p) => ({ ...p, currency: e.target.value }))}
              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div className="md:col-span-2 flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setCreateServiceOpen(false)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!serviceFormValid}
              onClick={() => setCreateServiceConfirmOpen(true)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold ${serviceFormValid
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "cursor-not-allowed bg-gray-200 text-gray-500"
                }`}
            >
              Continue
            </button>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        open={createServiceConfirmOpen}
        title="Confirm Service Creation"
        onClose={() => setCreateServiceConfirmOpen(false)}
      >
        <div className="space-y-3 text-[12px]">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="font-semibold text-gray-900">
              {serviceForm.service_code} — {serviceForm.service_name}
            </div>
            <div className="mt-1 text-[11px] text-gray-600 line-clamp-3">{serviceForm.service_desc}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge>{serviceForm.service_url || "No URL"}</Badge>
              <Badge>{serviceForm.currency || "INR"}</Badge>
              <Badge tone={String(serviceForm.is_active) === "1" ? "green" : "red"}>
                {String(serviceForm.is_active) === "1" ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setCreateServiceConfirmOpen(false)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              disabled={createServiceLoading}
              onClick={submitCreateService}
              className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold ${createServiceLoading
                ? "cursor-not-allowed bg-emerald-300 text-white"
                : "bg-emerald-600 text-white hover:bg-emerald-700"
                }`}
            >
              {createServiceLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Creating…
                </>
              ) : (
                "Confirm & Create"
              )}
            </button>
          </div>
        </div>
      </ModalShell>

      {/* ---------------- DELETE MODULE CONFIRM (3 STEP) ---------------- */}
      <DestructiveConfirm
        open={deleteModuleOpen}
        itemLabel="Module"
        nameToType={deleteModuleTarget?.module_name || ""}
        impactText="Deleting a module may break navigation, remove access for clients, and impact billing/plan mappings. Proceed only if you are absolutely sure."
        onCancel={() => {
          if (!deleteModuleLoading) {
            setDeleteModuleOpen(false);
            setDeleteModuleTarget(null);
          }
        }}
        onConfirm={doDeleteModule}
        loading={deleteModuleLoading}
      />

      {/* ---------------- DELETE SERVICE CONFIRM (3 STEP) ---------------- */}
      <DestructiveConfirm
        open={deleteServiceOpen}
        itemLabel="Service"
        nameToType={deleteServiceTarget?.service_name || ""}
        impactText="Deleting a service may remove permissions, break feature flows, and invalidate entitlement mappings for clients."
        onCancel={() => {
          if (!deleteServiceLoading) {
            setDeleteServiceOpen(false);
            setDeleteServiceTarget(null);
          }
        }}
        onConfirm={doDeleteService}
        loading={deleteServiceLoading}
      />

      {/* ---------------- CLIENT ENTITLEMENT CONFIRM ---------------- */}
      <ModalShell
        open={entConfirmOpen}
        title="Confirm Client Entitlement"
        onClose={() => setEntConfirmOpen(false)}
      >
        <div className="space-y-3 text-[12px]">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="text-[11px] text-gray-600">Client</div>
            <div className="font-semibold text-gray-900">
              {selectedClient
                ? `${selectedClient.us_code} — ${selectedClient.us_name || selectedClient.organization_name || "Client"}`
                : entForm.userId}
            </div>

            <div className="mt-2 text-[11px] text-gray-600">Module</div>
            <div className="font-semibold text-gray-900">
              {selectedModule?.module_name || entForm.moduleId}
            </div>

            <div className="mt-2 text-[11px] text-gray-600">Service</div>
            <div className="font-semibold text-gray-900">
              {selectedService
                ? `${selectedService.service_code} — ${selectedService.service_name}`
                : entForm.serviceId}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setEntConfirmOpen(false)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!entValid || entLoading}
              onClick={submitEntitlement}
              className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold ${!entValid || entLoading
                ? "cursor-not-allowed bg-gray-200 text-gray-500"
                : "bg-emerald-600 text-white hover:bg-emerald-700"
                }`}
            >
              {entLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Creating…
                </>
              ) : (
                "Confirm & Create"
              )}
            </button>
          </div>
        </div>
      </ModalShell>
    </div>
  );
};

export default AdminEntitlements;
